import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Loader2, Navigation, MapPin } from 'lucide-react';

const GOOGLE_MAPS_API_KEY = 'AIzaSyA6mzmjVd9FSUe3QoQmhdmu0x5nuC8grwk';

const LiveMap = ({ 
  pickup, 
  dropoff, 
  driverLocation = null,
  showRoute = false,
  showNavigation = false,
  onNavigationStep = null,
  className = '',
  height = '300px'
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const markersRef = useRef({});
  const directionsRendererRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [navigationSteps, setNavigationSteps] = useState([]);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);

  // Initialize map
  const initMap = useCallback(() => {
    if (!window.google || !mapRef.current) return;

    const center = pickup ? { lat: pickup.lat, lng: pickup.lng } : { lat: 40.7128, lng: -74.006 };
    
    const map = new window.google.maps.Map(mapRef.current, {
      center,
      zoom: 14,
      styles: [
        { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] },
        { featureType: 'transit', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] }
      ],
      disableDefaultUI: true,
      zoomControl: true,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false
    });

    mapInstanceRef.current = map;
    
    // Initialize directions renderer
    directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
      map,
      suppressMarkers: true,
      polylineOptions: {
        strokeColor: '#dc2626',
        strokeWeight: 5,
        strokeOpacity: 0.8
      }
    });

    setLoading(false);
  }, [pickup]);

  // Load Google Maps script
  useEffect(() => {
    if (window.google && window.google.maps) {
      initMap();
      return;
    }

    // Check if script is already loading
    if (document.querySelector('script[src*="maps.googleapis.com"]')) {
      const checkGoogleMaps = setInterval(() => {
        if (window.google && window.google.maps) {
          clearInterval(checkGoogleMaps);
          initMap();
        }
      }, 100);
      return () => clearInterval(checkGoogleMaps);
    }

    setLoading(false);
    setError('Google Maps is loading...');
  }, [initMap]);

  // Update markers
  useEffect(() => {
    if (!mapInstanceRef.current || !window.google) return;

    const map = mapInstanceRef.current;

    // Clear old markers
    Object.values(markersRef.current).forEach(marker => marker.setMap(null));
    markersRef.current = {};

    // Pickup marker
    if (pickup) {
      markersRef.current.pickup = new window.google.maps.Marker({
        position: { lat: pickup.lat, lng: pickup.lng },
        map,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#22c55e',
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: 3
        },
        title: 'Pickup Location'
      });
    }

    // Dropoff marker
    if (dropoff) {
      markersRef.current.dropoff = new window.google.maps.Marker({
        position: { lat: dropoff.lat, lng: dropoff.lng },
        map,
        icon: {
          path: window.google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: '#dc2626',
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: 3
        },
        title: 'Dropoff Location'
      });
    }

    // Driver marker
    if (driverLocation) {
      markersRef.current.driver = new window.google.maps.Marker({
        position: { lat: driverLocation.lat, lng: driverLocation.lng },
        map,
        icon: {
          url: 'data:image/svg+xml,' + encodeURIComponent(`
            <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 24 24">
              <circle cx="12" cy="12" r="10" fill="#dc2626"/>
              <path d="M12 4l-1.5 5h-5.5l4.5 3.5-1.5 5 4-3 4 3-1.5-5 4.5-3.5h-5.5z" fill="white"/>
            </svg>
          `),
          scaledSize: new window.google.maps.Size(40, 40),
          anchor: new window.google.maps.Point(20, 20)
        },
        title: 'Driver Location'
      });
    }

    // Fit bounds to show all markers
    if (Object.keys(markersRef.current).length > 0) {
      const bounds = new window.google.maps.LatLngBounds();
      Object.values(markersRef.current).forEach(marker => {
        bounds.extend(marker.getPosition());
      });
      map.fitBounds(bounds, { padding: 50 });
    }
  }, [pickup, dropoff, driverLocation]);

  // Draw route
  useEffect(() => {
    if (!showRoute || !pickup || !dropoff || !mapInstanceRef.current || !window.google) return;

    const directionsService = new window.google.maps.DirectionsService();

    directionsService.route(
      {
        origin: { lat: pickup.lat, lng: pickup.lng },
        destination: { lat: dropoff.lat, lng: dropoff.lng },
        travelMode: window.google.maps.TravelMode.DRIVING
      },
      (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK && directionsRendererRef.current) {
          directionsRendererRef.current.setDirections(result);
          
          // Extract navigation steps if needed
          if (showNavigation && result.routes[0]?.legs[0]?.steps) {
            const steps = result.routes[0].legs[0].steps.map((step, index) => ({
              index,
              instruction: step.instructions.replace(/<[^>]*>/g, ''),
              distance: step.distance.text,
              duration: step.duration.text,
              maneuver: step.maneuver || 'straight'
            }));
            setNavigationSteps(steps);
            if (onNavigationStep) {
              onNavigationStep(steps[0]);
            }
          }
        }
      }
    );
  }, [showRoute, pickup, dropoff, showNavigation, onNavigationStep]);

  // Navigate to next step
  const nextStep = () => {
    if (currentStepIndex < navigationSteps.length - 1) {
      const newIndex = currentStepIndex + 1;
      setCurrentStepIndex(newIndex);
      if (onNavigationStep) {
        onNavigationStep(navigationSteps[newIndex]);
      }
    }
  };

  if (error && !mapInstanceRef.current) {
    return (
      <div 
        className={`flex items-center justify-center bg-slate-100 dark:bg-slate-800 rounded-xl ${className}`}
        style={{ height }}
      >
        <div className="text-center p-4">
          <MapPin className="w-8 h-8 text-slate-400 mx-auto mb-2" />
          <p className="text-sm text-slate-500">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`}>
      {loading && (
        <div 
          className="absolute inset-0 flex items-center justify-center bg-slate-100 dark:bg-slate-800 rounded-xl z-10"
          style={{ height }}
        >
          <Loader2 className="w-8 h-8 animate-spin text-red-600" />
        </div>
      )}
      
      <div 
        ref={mapRef} 
        className="rounded-xl overflow-hidden"
        style={{ height }}
        data-testid="live-map"
      />

      {/* Navigation overlay */}
      {showNavigation && navigationSteps.length > 0 && (
        <div className="absolute bottom-4 left-4 right-4 bg-white dark:bg-slate-800 rounded-xl shadow-lg p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center flex-shrink-0">
              <Navigation className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-slate-900 dark:text-white text-sm truncate">
                {navigationSteps[currentStepIndex]?.instruction}
              </p>
              <p className="text-xs text-slate-500">
                {navigationSteps[currentStepIndex]?.distance} • {navigationSteps[currentStepIndex]?.duration}
              </p>
            </div>
            <button
              onClick={nextStep}
              disabled={currentStepIndex >= navigationSteps.length - 1}
              className="px-3 py-1 bg-red-600 text-white rounded-lg text-sm font-semibold disabled:opacity-50"
            >
              Next
            </button>
          </div>
          <div className="mt-2 flex gap-1">
            {navigationSteps.map((_, i) => (
              <div 
                key={i}
                className={`h-1 flex-1 rounded-full ${i <= currentStepIndex ? 'bg-red-600' : 'bg-slate-200 dark:bg-slate-700'}`}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default LiveMap;
